import type { Project, Task, TimeEntry, PaginatedResponse } from "@/types/time";
import { api } from "@/lib/api-client";

// Projects

export async function getProjects(params?: {
  includeArchived?: boolean;
  assigned?: boolean;
  page?: number;
  perPage?: number;
}) {
  const query = new URLSearchParams();
  if (params?.includeArchived) query.set("include_archived", "true");
  if (params?.assigned !== undefined) query.set("assigned", String(params.assigned));
  if (params?.page) query.set("page", String(params.page));
  if (params?.perPage) query.set("per_page", String(params.perPage));
  const qs = query.toString();
  return api<PaginatedResponse<Project>>(
    `/api/time/projects${qs ? `?${qs}` : ""}`
  );
}

export async function createProject(data: { name: string; color?: string }) {
  return api<Project>("/api/time/projects", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateProject(
  id: string,
  data: { name?: string; color?: string; isArchived?: boolean }
) {
  return api<Project>(`/api/time/projects/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function getProject(id: string) {
  return api<Project & { tasks: Task[] }>(`/api/time/projects/${id}`);
}

// Tasks

export async function createTask(projectId: string, data: { name: string }) {
  return api<Task>(`/api/time/projects/${projectId}/tasks`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateTask(
  projectId: string,
  taskId: string,
  data: { name?: string; isArchived?: boolean }
) {
  return api<Task>(`/api/time/projects/${projectId}/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

// Time Entries

export async function getEntries(
  params: {
    start_date?: string;
    end_date?: string;
    project_id?: string;
    task_id?: string;
    member_id?: string;
    member_ids?: string;
    team_id?: string;
    page?: number;
    per_page?: number;
  } = {}
) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const qs = query.toString();
  return api<PaginatedResponse<TimeEntry>>(`/api/time/entries${qs ? `?${qs}` : ""}`);
}

export async function createEntry(data: {
  description?: string | null;
  projectId?: string | null;
  taskId?: string | null;
  tagIds?: string[];
  memberId?: string;
  start: string;
  end?: string;
  createdWith: string;
}) {
  const body: Record<string, unknown> = {
    start: data.start,
    createdWith: data.createdWith,
  };
  if (data.end) body.end = data.end;
  if (data.description) body.description = data.description;
  if (data.projectId) body.projectId = data.projectId;
  if (data.taskId) body.taskId = data.taskId;
  if (data.tagIds?.length) body.tagIds = data.tagIds;
  if (data.memberId) body.memberId = data.memberId;

  return api<TimeEntry>("/api/time/entries", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateEntry(
  id: string,
  data: {
    description?: string | null;
    projectId?: string | null;
    taskId?: string | null;
    tagIds?: string[];
    start?: string;
    end?: string;
  }
) {
  const body: Record<string, unknown> = {};
  if (data.start !== undefined) body.start = data.start;
  if (data.end !== undefined) body.end = data.end;
  if (data.description !== undefined) body.description = data.description;
  if (data.projectId !== undefined) body.projectId = data.projectId;
  if (data.taskId !== undefined) body.taskId = data.taskId;
  if (data.tagIds !== undefined) body.tagIds = data.tagIds;

  return api<TimeEntry>(`/api/time/entries/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteEntry(id: string) {
  return api<{ message: string }>(`/api/time/entries/${id}`, {
    method: "DELETE",
  });
}

// Project Members

export async function getProjectMembers(projectId: string) {
  return api<{
    members: Array<{
      id: string;
      projectId: string;
      memberId: string;
      createdAt: string;
    }>;
  }>(`/api/time/projects/${projectId}/members`);
}

export async function addProjectMembers(projectId: string, memberIds: string[]) {
  return api<{
    members: Array<{
      id: string;
      projectId: string;
      memberId: string;
      createdAt: string;
    }>;
  }>(`/api/time/projects/${projectId}/members`, {
    method: "POST",
    body: JSON.stringify({ memberIds }),
  });
}

export async function removeProjectMember(projectId: string, memberId: string) {
  return api<{ id: string }>(`/api/time/projects/${projectId}/members/${memberId}`, {
    method: "DELETE",
  });
}
